import React, { useEffect, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('pt-PT');
  } catch {
    return value;
  }
};

const formatCurrency = (value) => `€${Number(value || 0).toFixed(2)}`;

const getStatusBadge = (status) => {
  const map = {
    pending: 'bg-amber-100 text-amber-800',
    paid: 'bg-emerald-100 text-emerald-800',
    cancelled: 'bg-muted text-muted-foreground',
    disputed: 'bg-red-100 text-red-800',
  };
  return map[status] || 'bg-muted text-muted-foreground';
};

const getStatusText = (status) => {
  const map = { pending: 'Pendente', paid: 'Paga', cancelled: 'Cancelada', disputed: 'Contestada' };
  return map[status] || status;
};

export default function FineManagement() {
  const [fines, setFines] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDriver, setFilterDriver] = useState('');
  const [formData, setFormData] = useState({
    driver_id: '',
    vehicle_id: '',
    fine_number: '',
    fine_type: '',
    amount: '',
    description: '',
    issue_date: '',
  });

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterDriver) params.driver_id = filterDriver;
      const [finesRes, driversRes, vehiclesRes] = await Promise.all([
        api.get('/fines', { params }),
        api.get('/drivers'),
        api.get('/vehicles'),
      ]);
      setFines(finesRes.data?.data || []);
      setDrivers(driversRes.data?.data || []);
      setVehicles(vehiclesRes.data?.data || []);
    } catch {
      toast.error('Erro ao carregar multas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterDriver]);

  const resetForm = () => {
    setFormData({
      driver_id: '',
      vehicle_id: '',
      fine_number: '',
      fine_type: '',
      amount: '',
      description: '',
      issue_date: '',
    });
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy('create');
    try {
      await api.post('/fines', formData);
      toast.success('Multa registada');
      setShowModal(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao registar multa');
    } finally {
      setBusy(null);
    }
  };

  const pay = async (id) => {
    if (!window.confirm('Confirma o pagamento desta multa?')) return;
    setBusy(id);
    try {
      await api.post(`/fines/${id}/pay`);
      toast.success('Multa paga');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao pagar multa');
    } finally {
      setBusy(null);
    }
  };

  const cancel = async (id) => {
    const reason = window.prompt('Motivo do cancelamento:');
    if (!reason) return;
    setBusy(id);
    try {
      await api.post(`/fines/${id}/cancel`, { reason });
      toast.success('Multa cancelada');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao cancelar multa');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
        <p className="mt-3 text-sm text-muted-foreground">Carregando Multas...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Gestão de Multas</h1>
        <Button onClick={() => setShowModal(true)}>
          <Plus size={16} className="mr-2" /> Nova Multa
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="text-sm font-medium">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                <option value="">Todos</option>
                <option value="pending">Pendente</option>
                <option value="paid">Paga</option>
                <option value="cancelled">Cancelada</option>
                <option value="disputed">Contestada</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Motorista</label>
              <select
                value={filterDriver}
                onChange={(e) => setFilterDriver(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                <option value="">Todos</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name || `${d.first_name} ${d.last_name}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setFilterStatus('');
                  setFilterDriver('');
                }}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Multas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2">Nº</th>
                  <th>Motorista</th>
                  <th>Tipo</th>
                  <th className="text-right">Valor</th>
                  <th>Data</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {fines.map((f) => (
                  <tr key={f.id} className="border-b">
                    <td className="py-2 font-medium">{f.fine_number}</td>
                    <td>{f.driver_name}</td>
                    <td>{f.fine_type || '—'}</td>
                    <td className="text-right">{formatCurrency(f.amount)}</td>
                    <td>{formatDate(f.issue_date)}</td>
                    <td>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadge(f.status)}`}>
                        {getStatusText(f.status)}
                      </span>
                    </td>
                    <td className="text-right">
                      {f.status === 'pending' && (
                        <div className="flex justify-end gap-2">
                          <Button size="sm" onClick={() => pay(f.id)} disabled={busy === f.id}>
                            {busy === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Pagar'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => cancel(f.id)} disabled={busy === f.id}>
                            Cancelar
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {fines.length === 0 && (
                  <tr>
                    <td colSpan="7" className="py-4 text-center text-sm text-muted-foreground">
                      Nenhuma multa registada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Registar Multa</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={create} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium">Motorista *</label>
                    <select
                      value={formData.driver_id}
                      onChange={(e) => setFormData({ ...formData, driver_id: e.target.value })}
                      required
                      className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                    >
                      <option value="">Selecione...</option>
                      {drivers.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name || `${d.first_name} ${d.last_name}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Veículo</label>
                    <select
                      value={formData.vehicle_id}
                      onChange={(e) => setFormData({ ...formData, vehicle_id: e.target.value })}
                      className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                    >
                      <option value="">Selecione...</option>
                      {vehicles.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.license_plate}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Número da Multa *</label>
                    <Input
                      value={formData.fine_number}
                      onChange={(e) => setFormData({ ...formData, fine_number: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Tipo</label>
                    <Input
                      value={formData.fine_type}
                      onChange={(e) => setFormData({ ...formData, fine_type: e.target.value })}
                      placeholder="Ex: Velocidade"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Valor (€) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Data *</label>
                    <Input
                      type="date"
                      value={formData.issue_date}
                      onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Descrição</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={busy === 'create'} className="flex-1">
                    {busy === 'create' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Registar'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
